import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Check, Sparkles, Loader2 } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { tokenService } from '@/services/payment';
import axios from 'axios';

const Pricing = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [serviceStatus, setServiceStatus] = useState({ working: false, checked: false });

  useEffect(() => {
    // Check if the payment service is available
    const checkServiceStatus = async () => {
      try {
        console.log('[PRICING] Checking payment service status...');
        const response = await axios.get('/api/creem/checkouts', { timeout: 3000 });
        if (response.status === 200) {
          console.log('[PRICING] Payment service is available:', response.data);
          setServiceStatus({ working: true, checked: true });
          
          // Show toast if API is working correctly
          if (response.data?.api_status === 'online') {
            toast({
              title: "Payment System Ready",
              description: "You can now purchase tokens",
            });
          }
        }
      } catch (error) {
        console.warn('[PRICING] Payment service status check failed:', error);
        setServiceStatus({ working: false, checked: true });
        
        // Try direct API health check as fallback
        try {
          const { CREEM_CONFIG } = await import('@/config/appConfig');
          const directCheck = await axios.get(`${CREEM_CONFIG.API_URL}/health`, {
            headers: { 'x-api-key': CREEM_CONFIG.API_KEY },
            timeout: 3000
          });
          if (directCheck.status === 200) {
            console.log('[PRICING] Direct API check successful');
            setServiceStatus({ working: true, checked: true });
            toast({
              title: "Payment System Ready",
              description: "You can now purchase tokens",
            });
          }
        } catch (directError) {
          console.error('[PRICING] Direct API check failed:', directError);
          toast({
            title: "Payment System Issue",
            description: "There might be issues with the payment system. Please try again later.",
            variant: "destructive",
          });
        }
      }
    };

    checkServiceStatus();
  }, []);

  /**
   * Handles payment for selected plan
   */
  const handlePurchase = async (plan: 'starter' | 'monthly' | 'team') => {
    // Ensure user is logged in
    if (!currentUser) {
      toast({
        title: "Login Required",
        description: "Please sign in to purchase tokens",
        variant: "destructive",
      });
      navigate('/?showAuth=true');
      return;
    }
    
    // Show loading state
    setLoadingPlan(plan);
    
    try {
      // Notify user
      toast({
        title: "Processing",
        description: "Preparing checkout...",
      });
      
      console.log(`[PURCHASE] Starting checkout for ${plan} plan`);
      
      // Get direct checkout URL
      const checkoutUrl = await tokenService.createCheckout(plan, currentUser.uid);
      
      if (!checkoutUrl) {
        throw new Error("Checkout URL not available");
      }
      
      console.log(`[PURCHASE] Redirecting to: ${checkoutUrl}`);
      
      // Redirect to Creem checkout
      setIsRedirecting(true);
      window.location.href = checkoutUrl;
    } catch (error) {
      // Reset loading state
      setLoadingPlan(null);
      setIsRedirecting(false);
      
      console.error("[PURCHASE] Error:", error);
      
      toast({
        title: "Checkout Error",
        description: error instanceof Error ? error.message : "Unable to start checkout",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      {!serviceStatus.working && serviceStatus.checked && (
        <div className="bg-red-500/10 text-red-500 py-2 px-4 text-center text-sm">
          Payment system is currently undergoing maintenance. Purchases may not be available at this time.
        </div>
      )}
      <div className="fixed inset-0 z-[-1] overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-background to-background/90"></div>
        <div className="absolute top-0 -left-40 w-96 h-96 bg-purple-600/10 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-0 -right-20 w-96 h-96 bg-indigo-600/10 rounded-full filter blur-3xl"></div>
        <div className="absolute top-1/3 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-fuchsia-600/5 rounded-full filter blur-3xl"></div>
      </div>
      
      <Navbar />
      
      <main className="flex-grow">
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h1 className="text-4xl font-bold text-center mb-4">
                Power Up Your PromptAssist Experience
              </h1>
              <p className="text-xl text-muted-foreground">
                Pay only for what you use. No hidden fees, just credits for perfect prompts.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="elevated-card border border-white/10 hover:border-purple-500/30 transition-all"
              >
                <div className="p-6">
                  <h3 className="text-lg font-medium mb-2">Starter</h3>
                  <div className="mb-4">
                    <span className="text-3xl font-bold">$5</span>
                    <span className="text-muted-foreground"> / 20 credits</span>
                  </div>
                  <p className="text-muted-foreground mb-6">
                    Perfect for trying out our prompt engineering capabilities.
                  </p>
                  
                  <ul className="space-y-3 mb-8">
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-purple-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span>20 prompt generations</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-purple-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span>Basic prompt templates</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-purple-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span>AI chat assistance</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-purple-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span>30-day credit validity</span>
                    </li>
                  </ul>
                  
                  <Button 
                    className="w-full bg-gradient-to-r from-purple-600 to-indigo-600"
                    onClick={() => handlePurchase('starter')}
                    disabled={loadingPlan === 'starter' || isRedirecting}
                  >
                    {loadingPlan === 'starter' ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      'Get Started'
                    )}
                  </Button>
                </div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="elevated-card border-2 border-purple-500/50 relative bg-gradient-to-b from-purple-900/20 to-background"
              >
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                  <span className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-xs font-medium px-3 py-1 rounded-full inline-flex items-center">
                    <Sparkles className="h-3 w-3 mr-1" />
                    Most Popular
                  </span>
                </div>
                
                <div className="p-6">
                  <h3 className="text-lg font-medium mb-2">Monthly</h3>
                  <div className="mb-4">
                    <span className="text-3xl font-bold">$20</span>
                    <span className="text-muted-foreground"> / 100 credits</span>
                  </div>
                  <p className="text-muted-foreground mb-6">
                    The perfect balance for active developers and teams.
                  </p>
                  
                  <ul className="space-y-3 mb-8">
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-purple-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span>100 prompt generations</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-purple-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span>Advanced prompt templates</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-purple-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span>Enhanced AI assistance</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-purple-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span>Prompt history & favorites</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-purple-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span>30-day credit validity</span>
                    </li>
                  </ul>
                  
                  <Button 
                    className="w-full bg-gradient-to-r from-purple-600 to-indigo-600"
                    onClick={() => handlePurchase('monthly')}
                    disabled={loadingPlan === 'monthly' || isRedirecting}
                  >
                    {loadingPlan === 'monthly' ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      'Choose Monthly'
                    )}
                  </Button>
                </div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="elevated-card border border-white/10 hover:border-purple-500/30 transition-all"
              >
                <div className="p-6">
                  <h3 className="text-lg font-medium mb-2">Team</h3>
                  <div className="mb-4">
                    <span className="text-3xl font-bold">$45</span>
                    <span className="text-muted-foreground"> / 250 credits</span>
                  </div>
                  <p className="text-muted-foreground mb-6">
                    Best value for teams with multiple developers.
                  </p>
                  
                  <ul className="space-y-3 mb-8">
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-purple-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span>250 prompt generations</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-purple-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span>Premium prompt templates</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-purple-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span>Priority AI assistance</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-purple-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span>Team collaboration features</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-purple-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span>60-day credit validity</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-purple-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span>Priority support</span>
                    </li>
                  </ul>
                  
                  <Button 
                    className="w-full bg-gradient-to-r from-purple-600 to-indigo-600"
                    onClick={() => handlePurchase('team')}
                    disabled={loadingPlan === 'team' || isRedirecting}
                  >
                    {loadingPlan === 'team' ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      'Choose Team'
                    )}
                  </Button>
                </div>
              </motion.div>
            </div>
            
            <div className="mt-16 max-w-3xl mx-auto bg-gradient-to-r from-purple-900/20 to-indigo-900/20 p-6 md:p-8 rounded-xl border border-white/10">
              <h3 className="text-xl font-semibold mb-4 text-center">
                The Power of AI Prompt Engineering
              </h3>
              <p className="text-muted-foreground text-center mb-6">
                Our credit system lets you engineer prompts that save hours of development time. Perfect prompts mean clearer communication with AI models and better development outcomes.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start">
                  <Check className="h-5 w-5 text-purple-500 mr-2 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium">Design to Features</h4>
                    <p className="text-sm text-muted-foreground">Prompts cover everything from UI design to core functionality</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <Check className="h-5 w-5 text-purple-500 mr-2 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium">AI Optimization</h4>
                    <p className="text-sm text-muted-foreground">Prompts designed to get the best from AI models</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <Check className="h-5 w-5 text-purple-500 mr-2 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium">Development Accelerator</h4>
                    <p className="text-sm text-muted-foreground">Build applications faster with clear guidance</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <Check className="h-5 w-5 text-purple-500 mr-2 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium">Maximize Value</h4>
                    <p className="text-sm text-muted-foreground">Each prompt saves hours of development time</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default Pricing;
